import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, ArrowRight, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { getModuleIcon, getFamiliesForModule, getAllowedTiersForGroup, type ModuleId } from '../../../content';
import { getFamilyMasteryKey } from '../../../mastery';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { humanizeFamilyId } from '../../../content';
interface SubjectsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
}
function getClassFamilyStats(students: StudentData[], moduleId: string, selectedGroup: number) {
  const families = getFamiliesForModule(moduleId as ModuleId);
  const allowedTiers = getAllowedTiersForGroup(selectedGroup as GroupId);
  const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
  return relevantFamilies.map(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    let totalMastery = 0;
    let practicedCount = 0;
    const strugglingStudents: Array<{
      id: string;
      name: string;
      mastery: number;
    }> = [];
    students.forEach(s => {
      const fm = s.data.familyMastery?.[key];
      if (fm && fm.attempts > 0) {
        practicedCount++;
        totalMastery += fm.masteryScore;
        if (fm.masteryScore < 40) {
          strugglingStudents.push({
            id: s.id,
            name: s.name,
            mastery: Math.round(fm.masteryScore)
          });
        }
      }
    });
    const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
    return {
      familyId: f.familyId,
      label: humanizeFamilyId(f.familyId),
      avgMastery,
      practicedCount,
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery)
    };
  }).filter(f => f.practicedCount > 0).sort((a, b) => b.avgMastery - a.avgMastery);
}
function ExpandedModulePanel({
  moduleId,
  students,
  selectedGroup,
  strugglingStudents,
  onSelectStudent
}: {
  moduleId: string;
  students: StudentData[];
  selectedGroup: number;
  strugglingStudents: Array<{
    id: string;
    name: string;
    mastery: number;
  }>;
  onSelectStudent: (id: string) => void;
}) {
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const familyStats = useMemo(() => {
    return getClassFamilyStats(students, moduleId, selectedGroup);
  }, [students, moduleId, selectedGroup]);
  return <motion.div initial={{
    height: 0,
    opacity: 0
  }} animate={{
    height: 'auto',
    opacity: 1
  }} exit={{
    height: 0,
    opacity: 0
  }} className="overflow-hidden bg-slate-50/50" data-id="element-7051">
      <div className="pl-14 pr-4 py-4 border-t border-slate-100 space-y-6" data-id="element-7052">
        {/* Family Table */}
        {familyStats.length > 0 && <div className="space-y-3" data-id="element-7053">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-7054">
              Onderwerpen ({familyStats.length})
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-7055">
              <div className="divide-y divide-slate-100" data-id="element-7056">
                {familyStats.map(f => {
              const isFamilyExpanded = selectedFamily === f.familyId;
              return <div key={f.familyId} className="flex flex-col" data-id="element-7057">
                      <div className="p-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelectedFamily(isFamilyExpanded ? null : f.familyId)} data-id="element-7058">
                        <div className="flex-1 min-w-0" data-id="element-7059">
                          <div className="text-sm font-bold text-slate-900 truncate" data-id="element-7060">
                            {f.label}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-7061">
                            {f.practicedCount} leerlingen
                          </div>
                        </div>
                        <div className="w-32 flex items-center gap-2" data-id="element-7062">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-7063">
                            <div className={`h-full rounded-full ${f.avgMastery >= 75 ? 'bg-emerald-500' : f.avgMastery >= 40 ? 'bg-indigo-400' : 'bg-rose-500'}`} style={{
                        width: `${Math.max(2, f.avgMastery)}%`
                      }} data-id="element-7064" />
                          </div>
                          <div className={`w-8 text-right text-xs font-bold ${f.avgMastery >= 75 ? 'text-emerald-600' : f.avgMastery >= 40 ? 'text-indigo-600' : 'text-rose-600'}`} data-id="element-7065">
                            {f.avgMastery}%
                          </div>
                        </div>
                        <div className="w-6 flex justify-end text-slate-400" data-id="element-7066">
                          {isFamilyExpanded ? <ChevronUp className="w-4 h-4" data-id="element-7067" /> : <ChevronDown className="w-4 h-4" data-id="element-7068" />}
                        </div>
                      </div>

                      <AnimatePresence data-id="element-7069">
                        {isFamilyExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-7070">
                            <div className="p-3 pl-4" data-id="element-7071">
                              {f.strugglingStudents.length === 0 ? <div className="text-xs font-medium text-emerald-600" data-id="element-7072">
                                  Geen leerlingen met moeite.
                                </div> : <div className="space-y-2" data-id="element-7073">
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-7074">
                                    Aandacht nodig (
                                    {f.strugglingStudents.length})
                                  </div>
                                  <div className="flex flex-wrap gap-2" data-id="element-7075">
                                    {f.strugglingStudents.map(s => {
                            const initial = s.name.charAt(0).toUpperCase();
                            return <div key={s.id} onClick={e => {
                              e.stopPropagation();
                              onSelectStudent(s.id);
                            }} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-indigo-200 cursor-pointer shadow-sm transition-colors" data-id="element-7076">
                                          <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold" data-id="element-7077">
                                            {initial}
                                          </div>
                                          <span className="text-xs font-bold text-slate-900" data-id="element-7078">
                                            {s.name}
                                          </span>
                                          <span className="text-xs font-black text-rose-600" data-id="element-7079">
                                            {s.mastery}%
                                          </span>
                                        </div>;
                          })}
                                  </div>
                                </div>}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            })}
              </div>
            </div>
          </div>}

        {/* Module Struggling Students */}
        {strugglingStudents.length === 0 ? <div className="text-sm font-medium text-emerald-600 flex items-center gap-2" data-id="element-7080">
            Alle leerlingen op schema voor dit vak ✓
          </div> : <div className="space-y-2" data-id="element-7081">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-7082">
              Aandacht nodig voor hele vak ({strugglingStudents.length})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-id="element-7083">
              {strugglingStudents.slice(0, 5).map(s => {
            const initial = s.name.charAt(0).toUpperCase();
            return <div key={s.id} onClick={e => {
              e.stopPropagation();
              onSelectStudent(s.id);
            }} className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-200 cursor-pointer transition-colors" data-id="element-7084">
                    <div className="flex items-center gap-2" data-id="element-7085">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-7086">
                        {initial}
                      </div>
                      <span className="text-sm font-bold text-slate-900 truncate max-w-[100px]" data-id="element-7087">
                        {s.name}
                      </span>
                    </div>
                    <div className="text-xs font-black text-rose-600" data-id="element-7088">
                      {s.mastery}%
                    </div>
                  </div>;
          })}
            </div>
            {strugglingStudents.length > 5 && <div className="text-xs font-medium text-slate-500 mt-2" data-id="element-7089">
                + {strugglingStudents.length - 5} meer
              </div>}
          </div>}
      </div>
    </motion.div>;
}
export function SubjectsPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent
}: SubjectsPageProps) {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const data = useMemo(() => {
    if (students.length === 0) return null;
    const moduleStats = getClassModuleStats(students, selectedGroup, getModuleMastery);
    // Sort by "needs attention": declining trend first, then lowest mastery
    const sortedStats = [...moduleStats].sort((a, b) => {
      // Primary sort: declining trend
      if (a.trend === 'down' && b.trend !== 'down') return -1;
      if (b.trend === 'down' && a.trend !== 'down') return 1;
      // Secondary sort: lowest mastery
      return a.avgMastery - b.avgMastery;
    });
    return {
      moduleStats: sortedStats
    };
  }, [students, getModuleMastery, selectedGroup]);
  if (!data) {
    return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-7090">
        <EmptyState title="Geen data beschikbaar" message="Er is nog geen data beschikbaar voor deze klas." emotion="thinking" colorScheme="slate" data-id="element-7091" />
      </PageLayout>;
  }
  return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-7092">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-7093">
        {/* Module Bars */}
        <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-7094">
          <div className="divide-y divide-slate-100" data-id="element-7095">
            {data.moduleStats.map((stat, i) => {
            const IconComp = getModuleIcon(stat.moduleIcon);
            const total = students.length;
            const isExpanded = selectedModule === stat.moduleId;
            const strugglingStudents = students.map(s => ({
              ...s,
              mastery: getModuleMastery(s.id, stat.moduleId)
            })).filter(s => s.mastery < 40).sort((a, b) => a.mastery - b.mastery);
            const getIconColors = (mastery: number) => {
              if (mastery < 40) return 'bg-rose-100 text-rose-600';
              if (mastery < 70) return 'bg-amber-100 text-amber-600';
              return 'bg-emerald-100 text-emerald-600';
            };
            return <motion.div key={stat.moduleId} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.03
            }} className="hover:bg-slate-50 transition-colors" data-id="element-7096">
                  <div className="p-3 flex items-center gap-4 cursor-pointer" role="button" tabIndex={0} aria-expanded={isExpanded} aria-label={`${stat.moduleTitle} — ${stat.avgMastery}% gemiddeld${isExpanded ? ', ingeklapt' : ', klik om uit te klappen'}`} onClick={() => setSelectedModule(isExpanded ? null : stat.moduleId)} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedModule(isExpanded ? null : stat.moduleId);
                }
              }} data-id="element-7097">
                    <div className="flex items-center gap-3 w-48 flex-shrink-0" data-id="element-7098">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getIconColors(stat.avgMastery)}`} data-id="element-7099">
                        <IconComp className="w-4 h-4" data-id="element-7100" />
                      </div>
                      <div className="min-w-0" data-id="element-7101">
                        <div className="font-bold text-sm text-slate-900 truncate" data-id="element-7102">
                          {stat.moduleTitle}
                        </div>
                        <div className="text-[10px] font-medium text-slate-500" data-id="element-7103">
                          {stat.practicedCount > 0 ? `${stat.practicedCount} leerlingen` : 'Nog niet geoefend'}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 flex items-center gap-4" data-id="element-7104">
                      {stat.practicedCount === 0 ? <div className="flex-1 flex items-center" data-id="element-7105">
                          <span className="text-xs font-medium text-slate-400 italic" data-id="element-7106">
                            Geen oefendata voor deze groep
                          </span>
                        </div> : <div className="flex-1 space-y-1" data-id="element-7107">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-7108">
                            {/* Distribution segments */}
                            {stat.distribution.excellent > 0 && <div className="h-full bg-emerald-500" style={{
                        width: `${stat.distribution.excellent / total * 100}%`
                      }} title={`${stat.distribution.excellent} uitstekend`} data-id="element-7109" />}
                            {stat.distribution.good > 0 && <div className="h-full bg-indigo-400" style={{
                        width: `${stat.distribution.good / total * 100}%`
                      }} title={`${stat.distribution.good} goed`} data-id="element-7110" />}
                            {stat.distribution.attention > 0 && <div className="h-full bg-rose-500" style={{
                        width: `${stat.distribution.attention / total * 100}%`
                      }} title={`${stat.distribution.attention} aandacht nodig`} data-id="element-7111" />}
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" title={`Klasgemiddelde: ${stat.avgMastery}%`} data-id="element-7112">
                            <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                        width: `${Math.max(2, stat.avgMastery)}%`
                      }} data-id="element-7113" />
                          </div>
                        </div>}
                      <div className="w-10 text-right text-base font-black text-slate-900" data-id="element-7114">
                        {stat.practicedCount > 0 ? `${stat.avgMastery}%` : '—'}
                      </div>
                      <div className="w-24 flex justify-end items-center gap-2" data-id="element-7115">
                        {stat.trend === 'down' && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded text-[10px] font-bold uppercase tracking-wider" data-id="element-7116">
                            <AlertTriangle className="w-3 h-3" data-id="element-7117" />
                            Aandacht
                          </div>}
                        {stat.trend === 'up' && <ArrowUpRight className="w-4 h-4 text-emerald-500" data-id="element-7118" />}
                        {stat.trend === 'stable' && <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-7119" />}
                      </div>
                      <div className="w-6 flex justify-end text-slate-400" data-id="element-7120">
                        {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-7121" /> : <ChevronDown className="w-5 h-5" data-id="element-7122" />}
                      </div>
                    </div>
                  </div>

                  <AnimatePresence data-id="element-7123">
                    {isExpanded && <ExpandedModulePanel moduleId={stat.moduleId} students={students} selectedGroup={selectedGroup} strugglingStudents={strugglingStudents} onSelectStudent={onSelectStudent} data-id="element-7124" />}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
          <div className="bg-slate-50 p-3 border-t border-slate-100 flex justify-center gap-6 text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-7125">
            <div className="flex items-center gap-1.5" data-id="element-7126">
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" data-id="element-7127" />{' '}
              Uitstekend
            </div>
            <div className="flex items-center gap-1.5" data-id="element-7128">
              <div className="w-2.5 h-2.5 rounded-sm bg-indigo-400" data-id="element-7129" /> Goed
            </div>
            <div className="flex items-center gap-1.5" data-id="element-7130">
              <div className="w-2.5 h-2.5 rounded-sm bg-rose-500" data-id="element-7131" /> Aandacht
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>;
}